import {
  X,
  Flag,
  Trophy,
  ChartNoAxesColumnIncreasing,
  Film,
  Shield,
  LockKeyhole,
  Plus,
} from "lucide-react";
import { slotsForMatch, SLOT_LABELS } from "@/features/matches/matchSlots";
import { TournamentEditor } from "@/features/tournaments/TournamentEditor";
import { useTournaments } from "@/features/tournaments/useTournaments";
import { useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm, type SubmitHandler } from "react-hook-form";
import { z } from "zod";
import {
  ApiError,
  createMatch,
  updateMatch,
  uploadMatchOpponentLogo,
} from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { TkticDateInput } from "@/components/ui/TkticDateInput";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { useToast } from "@/components/ui/toast";
import { unscopedKey } from "@/lib/queryClient";
import { useAuthStore } from "@/stores/auth";
import { useScopeStore } from "@/stores/scope";
import { shootoutWinnerText } from "@/features/matches/matchResult";
import { getOpponentLogoUrl } from "@/lib/matchLogos";
import { OpponentLogoField } from "./OpponentLogoField";
import type { Match, Season, TournamentDraft, Tournament } from "@/lib/types";

/** Which side of the matchup is the active team playing on? Drives the
 * `home_team_id` / `away_team_id` columns persisted via PR #52a (the
 * structured Home/Away signal). "none" is the legitimate "external
 * scrimmage where neither side is this tenant" answer — leaves both
 * ids null and falls back to the legacy string match. */
type TeamSide = "home" | "away" | "none";

/**
 * MatchEditModal — shared create / edit dialog for matches (M12b).
 *
 * Originally the M8a `MatchEditorDialog` inside `AdminMatchesPage`; lifted
 * out so the coach Matches tab (`/coach?tab=matches`) and the admin
 * Matches page can share one form + one Zod schema + one mutation flow.
 *
 * Team scoping: the dialog itself does NOT render a team picker. The
 * `POST /api/matches` handler resolves the target team from the caller's
 * active scope (Scope store + the tenancy guard). For coaches that means
 * the match is locked to their active team; for global admins the active
 * team is whatever they've selected in /admin/teams. A future ticket can
 * add an inline team dropdown for admins — out of scope for M12b per the
 * parity doc.
 *
 * Cache invalidation: every consumer's matches cache is bumped on save —
 * `public-matches` (season view + per-team-slug variants),
 * `admin-matches` (the global-admin admin list), `admin-matches-scoped`
 * (the per-team admin list), and `coach-matches` (the M12b coach list).
 */
export interface MatchEditModalProps {
  match: Match | null;
  onClose: () => void;
  /** Explicit teams this modal may assign a match to. Admin Matches
   * passes this so create/edit no longer depends on the global active
   * scope control. Omit on coach surfaces to keep the active-team flow. */
  teamOptions?: Array<{ id: string; name: string }>;
  /** Optional callback fired after a successful save — used by the
   * caller to (e.g.) navigate to the new match or open the upload
   * flow. The mutation's TanStack invalidation has already completed
   * before this fires. */
  onSaved?: (match: Match) => void;
  /** Tournament names this team has already used, for the free-text
   * autocomplete. Callers pass names derived from the match list they
   * already hold — no extra fetch, and no new cache key to scope. */
  knownTournaments?: string[];
}

const optionalScore = z.number().int().min(0).nullable();

/** Chip-group options for the "decided by penalty shootout" toggle.
 * Two chips rather than a native checkbox — see `PenaltyShootoutField`. */
const PK_TOGGLE_OPTIONS: Array<{ value: boolean; label: string }> = [
  { value: false, label: "No" },
  { value: true, label: "Yes" },
];

/** Regulation ended level — the precondition for a penalty shootout.
 * Both scores must be present; a blank score line isn't a draw. */
export function isLevelScore(
  scoreHome: number | null,
  scoreAway: number | null,
): boolean {
  return scoreHome != null && scoreAway != null && scoreHome === scoreAway;
}

const matchFormSchema = z
  .object({
    home_team: z.string().trim().min(1, "Home team is required").max(200),
    away_team: z.string().trim().min(1, "Away team is required").max(200),
    date: z
      .string()
      .trim()
      .regex(/^$|^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD"),
    time: z
      .string()
      .trim()
      .regex(/^$|^\d{2}:\d{2}$/, "Use HH:MM"),
    location: z.string().trim().max(500),
    score_home: optionalScore,
    score_away: optionalScore,
    /** Competition (0052). Only `league` counts toward the season
     * record. Empty strings for the tournament fields rather than null
     * because they are bound to text inputs / a select. */
    competition: z.enum(["league", "tournament", "friendly"]),
    match_status: z.enum(["scheduled", "completed", "postponed", "cancelled"]),
    tournament_id: z.string(),
    stage: z.string().trim().max(200),
    tournament_name: z.string().trim().max(200),
    tournament_outcome: z.enum([
      "",
      "champions",
      "finalist",
      "semi_finalist",
      "third_place",
      "group_stage",
    ]),
    /** Penalty shootout (0051). Only reachable when regulation ended
     * level — the inputs are hidden otherwise, and `cleanMatchPayload`
     * drops the values so a score correction can't leave an orphaned
     * shootout behind. */
    pk_home: optionalScore,
    pk_away: optionalScore,
    /** Did this level match go to penalties? Explicit rather than
     * inferred from the tallies: most draws simply end, so "level
     * score" must not imply "shootout". Unchecking clears the tallies. */
    went_to_pens: z.boolean(),
    format: z.enum(["full", "separate"]),
    /** Which side of the matchup is OUR team on? Defaulted from a
     * case-insensitive match of `team.name` against `home_team` /
     * `away_team`, but the coach can override (string drift,
     * ambiguous name, external scrimmage). */
    team_side: z.enum(["home", "away", "none"]),
    /** Season the match belongs to.
     *
     * Optional in the form even though the backend persists a season on
     * every match. When no seasons exist yet (a brand-new install before
     * the admin has created any), the field is hidden and the backend's
     * `_resolve_match_write_scope` fallback assigns the default season.
     * A required
     * `.min(1)` here would silently fail submit with no visible field —
     * letting the backend own the fallback is the safer split. When the
     * field IS rendered, the `<select>` always has at least one option,
     * so an empty value only happens in the no-seasons-available case. */
    season_id: z.string().optional(),
  })
  .refine(
    (v) =>
      (v.score_home == null && v.score_away == null) ||
      (v.score_home != null && v.score_away != null),
    { message: "Enter both scores or leave both blank", path: ["score_away"] },
  )
  .refine(
    (v) =>
      v.match_status === "completed"
        ? v.score_home != null && v.score_away != null
        : v.score_home == null &&
          v.score_away == null &&
          v.pk_home == null &&
          v.pk_away == null,
    {
      message:
        "Completed requires both scores. Other statuses require empty scores and penalties.",
      path: ["match_status"],
    },
  )
  .refine((v) => !v.tournament_outcome || v.match_status === "completed", {
    message: "A tournament finish requires a completed match",
    path: ["tournament_outcome"],
  })
  // The shootout rules only bite when the coach ticked "decided by
  // penalties" — an untouched draw stays valid with empty PK inputs.
  .refine(
    (v) =>
      !v.went_to_pens ||
      !isLevelScore(v.score_home, v.score_away) ||
      (v.pk_home != null && v.pk_away != null),
    { message: "Enter both penalty tallies", path: ["pk_away"] },
  )
  .refine(
    (v) =>
      !v.went_to_pens ||
      v.pk_home == null ||
      v.pk_away == null ||
      v.pk_home !== v.pk_away,
    {
      message: "A shootout can't end level — enter the winning tally",
      path: ["pk_away"],
    },
  );

type MatchFormValues = z.infer<typeof matchFormSchema>;

export function defaultsForMatch(
  match: Match | null,
  scopeTeamId: string | null,
  scopeTeamName: string,
  scopeSeasonId: string | null,
  availableSeasons: Season[],
): MatchFormValues {
  return {
    home_team: match?.home_team ?? scopeTeamName,
    away_team: match?.away_team ?? "",
    date: match?.date ?? "",
    time: match?.time ?? "",
    location: match?.location ?? "",
    score_home: match?.score_home ?? null,
    score_away: match?.score_away ?? null,
    competition: match?.competition ?? "league",
    match_status: match?.match_status ?? "scheduled",
    tournament_id: match?.tournament_id ?? "",
    stage: match?.stage ?? "",
    tournament_name: match?.tournament_name ?? "",
    tournament_outcome: match?.tournament_outcome ?? "",
    pk_home: match?.pk_home ?? null,
    pk_away: match?.pk_away ?? null,
    // A stored shootout is the only signal that this match went to
    // penalties — there's no separate flag on the row.
    went_to_pens: match?.pk_home != null && match?.pk_away != null,
    format:
      match?.format === "two_halves" || match?.format === "separate"
        ? "separate"
        : "full",
    team_side: match
      ? deriveDefaultTeamSide(match, scopeTeamId, scopeTeamName)
      : scopeTeamId
        ? "home"
        : "none",
    season_id:
      match?.season_id ?? scopeSeasonId ?? availableSeasons[0]?.id ?? "",
  };
}

/** Derive the initial Home/Away/None radio value for an existing or
 * brand-new match:
 *
 *   1. If the row already has a `home_team_id` / `away_team_id` that
 *      matches the active scope's team, use that — explicit data
 *      always wins.
 *   2. Otherwise fall back to a case-insensitive string match between
 *      the active team's name and the home/away display strings (the
 *      classic legacy heuristic; carries the same fragility the
 *      structured ids were introduced to fix).
 *   3. If neither side matches, default to "none" — the coach can
 *      explicitly pick a side via the radio. */
function deriveDefaultTeamSide(
  match: Match | null,
  scopeTeamId: string | null,
  scopeTeamName: string,
): TeamSide {
  if (!scopeTeamId) return "none";
  if (match?.home_team_id && match.home_team_id === scopeTeamId) return "home";
  if (match?.away_team_id && match.away_team_id === scopeTeamId) return "away";
  const lowerName = scopeTeamName.trim().toLowerCase();
  if (!lowerName) return "none";
  if ((match?.home_team ?? "").trim().toLowerCase() === lowerName)
    return "home";
  if ((match?.away_team ?? "").trim().toLowerCase() === lowerName)
    return "away";
  return "none";
}

export function cleanMatchPayload(
  values: MatchFormValues,
  scopeTeamId: string | null,
  options: { includeTeamSide: boolean } = { includeTeamSide: true },
) {
  // Fold the Home/Away/None radio into the two id columns. The
  // coach's choice always wins over the auto-populate the server
  // does — we send explicit ids (or explicit nulls) so the backend
  // skips its string-match auto-populate path entirely.
  //
  // CAUTION on the UPDATE path: when the user did NOT touch the
  // radio, OMIT both id keys entirely so the backend's
  // `exclude_unset=True` path preserves whatever was already there
  // (e.g. a backfilled opponent id set when both teams are tktic
  // tenants). Sending an explicit `null` for the side the team
  // isn't on would silently clobber the opponent's structured id.
  // The caller derives `includeTeamSide` from the form's
  // `dirtyFields.team_side` (always true on create — there's
  // nothing to preserve yet).
  // Only send season_id when the user actually has a value to send.
  // For the no-seasons-available case (global admin without team
  // memberships), the field is hidden and `season_id` is undefined /
  // empty — let the backend's `_resolve_match_write_scope` fallback
  // assign the active scope's default season for the resolved team.
  const base = {
    home_team: values.home_team.trim(),
    away_team: values.away_team.trim(),
    date: values.date.trim(),
    time: values.time.trim(),
    location: values.location.trim(),
    score_home: values.score_home,
    score_away: values.score_away,
    competition: values.competition,
    match_status: values.match_status,
    // Only a tournament match may carry these; the API rejects the
    // combination otherwise. Empty string -> null so a cleared field
    // actually clears the column rather than storing "".
    ...(values.competition === "tournament"
      ? {
          tournament_id: values.tournament_id || null,
          stage: values.stage.trim() || null,
          tournament_name: values.tournament_name.trim() || null,
          tournament_outcome: values.tournament_outcome || null,
        }
      : {
          tournament_id: null,
          stage: null,
          tournament_name: null,
          tournament_outcome: null,
        }),
    // Send the shootout only when it's still coherent — the match ended
    // level AND the coach ticked the box. Otherwise send explicit nulls
    // rather than omitting the keys: correcting 2-2 to 3-1 has to CLEAR
    // a previously-saved shootout, and `exclude_unset` on the backend
    // means an absent key leaves the old value in place.
    ...(values.went_to_pens &&
    isLevelScore(values.score_home, values.score_away)
      ? { pk_home: values.pk_home, pk_away: values.pk_away }
      : { pk_home: null, pk_away: null }),
    format: values.format,
    ...(values.season_id ? { season_id: values.season_id } : {}),
  };
  if (!options.includeTeamSide) {
    return base;
  }
  let home_team_id: string | null = null;
  let away_team_id: string | null = null;
  if (scopeTeamId) {
    if (values.team_side === "home") {
      home_team_id = scopeTeamId;
    } else if (values.team_side === "away") {
      away_team_id = scopeTeamId;
    }
  }
  return {
    ...base,
    home_team_id,
    away_team_id,
  };
}

/** Invalidate every matches-cache the app surfaces. Centralized so a new
 * matches consumer only needs to add one entry here.
 *
 * TanStack Query treats query keys as prefixes for invalidation, so
 * `unscopedKey("public-matches")` matches BOTH the bare
 * `["public-matches"]` season-view cache AND the per-team-slug variants
 * `["public-matches", { teamSlug }]` used by `MatchGrid` /
 * `TeamMatchesPage`. We don't need to enumerate slug variants. */
async function invalidateAllMatchesCaches(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  await Promise.all([
    queryClient.invalidateQueries({
      predicate: (q) =>
        ["team-performance", "team-results", "tournaments", "matches"].includes(
          String(q.queryKey[0]),
        ),
    }),
    queryClient.invalidateQueries({ queryKey: unscopedKey("public-matches") }),
    queryClient.invalidateQueries({ queryKey: unscopedKey("admin-matches") }),
    queryClient.invalidateQueries({
      queryKey: unscopedKey("admin-matches-allowed"),
    }),
    queryClient.invalidateQueries({ queryKey: ["admin-matches-scoped"] }),
    queryClient.invalidateQueries({ queryKey: ["coach-matches"] }),
  ]);
}

function messageForApiError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    return typeof err.detail === "string" ? err.detail : fallback;
  }
  return fallback;
}

function parseOptionalScore(value: unknown): number | null {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

const fieldClassName =
  "w-full rounded border border-rule bg-canvas px-2 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent";

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-ink-muted">
      <span className="font-display uppercase tracking-wide">{label}</span>
      {children}
      {error ? (
        <span className="font-mono text-[0.65rem] text-destructive">
          {error}
        </span>
      ) : null}
    </label>
  );
}

export function MatchEditModal({
  match,
  onClose,
  teamOptions,
  onSaved,
  knownTournaments = [],
}: MatchEditModalProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const isEdit = !!match;
  // Active team drives the Home/Away radio default. A global admin
  // with no resolved scope (the seeded admin-global on a fresh
  // install) doesn't see the radio at all — the modal can't
  // attribute a side without knowing the team. They can edit other
  // fields fine; the side stays whatever the legacy string match
  // implies until a coach with a team takes over.
  const scopeTeamId = useScopeStore((s) => s.teamId);
  const scopeTeamName = useScopeStore((s) => s.active?.team?.name ?? "");
  const scopeSeasonId = useScopeStore((s) => s.seasonId);
  const hasExplicitTeamOptions = Array.isArray(teamOptions);
  const sortedTeamOptions = useMemo(
    () => [...(teamOptions ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [teamOptions],
  );
  const teamSelectOptions = useMemo(
    () => [
      ...(!isEdit && sortedTeamOptions.length > 1
        ? [{ value: "", label: "Pick a team", disabled: true }]
        : []),
      ...sortedTeamOptions.map((team) => ({
        value: team.id,
        label: team.name,
      })),
    ],
    [isEdit, sortedTeamOptions],
  );
  const initialExplicitTeamId =
    match?.team_id ??
    (sortedTeamOptions.length === 1 ? sortedTeamOptions[0]?.id : "") ??
    "";
  const [selectedTeamId, setSelectedTeamId] = useState(initialExplicitTeamId);
  useEffect(() => {
    setSelectedTeamId(initialExplicitTeamId);
  }, [initialExplicitTeamId]);
  const selectedTeam = hasExplicitTeamOptions
    ? sortedTeamOptions.find((team) => team.id === selectedTeamId)
    : null;
  const effectiveTeamId = hasExplicitTeamOptions
    ? selectedTeamId || null
    : scopeTeamId;
  const effectiveTeamName = hasExplicitTeamOptions
    ? (selectedTeam?.name ?? "")
    : scopeTeamName;
  const me = useAuthStore((s) => s.me);
  // Available seasons for the season picker. Seasons are globally
  // shared, so they come straight from the cached /api/me payload's
  // top-level `seasons` array. Sorted newest first (by starts_on desc
  // with created_at as tiebreaker) to mirror the backend ordering in
  // services/teams.py.
  const availableSeasons = useMemo<Season[]>(() => {
    if (!me) return [];
    return [...me.seasons].sort((a, b) => {
      const aKey = a.starts_on || a.created_at;
      const bKey = b.starts_on || b.created_at;
      if (aKey === bKey) return 0;
      return aKey < bKey ? 1 : -1;
    });
  }, [me]);
  const dialogRef = useRef<HTMLFormElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const defaults = useMemo(
    () =>
      defaultsForMatch(
        match,
        effectiveTeamId,
        effectiveTeamName,
        scopeSeasonId,
        availableSeasons,
      ),
    [
      match,
      effectiveTeamId,
      effectiveTeamName,
      scopeSeasonId,
      availableSeasons,
    ],
  );
  const form = useForm<MatchFormValues>({
    resolver: zodResolver(matchFormSchema),
    defaultValues: defaults,
  });

  const [eventDraft, setEventDraft] = useState<TournamentDraft | undefined>();
  const [editingEvent, setEditingEvent] = useState<
    TournamentDraft | null | undefined
  >();
  const selectedSeason = form.watch("season_id");
  const events = useTournaments(effectiveTeamId, selectedSeason);
  const eventScopeRef = useRef(`${effectiveTeamId}:${selectedSeason}`);
  useEffect(() => {
    const next = `${effectiveTeamId}:${selectedSeason}`;
    if (eventScopeRef.current !== next) {
      form.setValue("tournament_id", "", { shouldDirty: true });
      setEventDraft(undefined);
      setEditingEvent(undefined);
      eventScopeRef.current = next;
    }
  }, [effectiveTeamId, selectedSeason, form]);

  useEffect(() => {
    form.reset(defaults);
    setError(null);
  }, [form, defaults]);

  useEffect(() => {
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const firstField = dialogRef.current?.querySelector<HTMLElement>(
      "input, select, textarea, button:not([disabled]), a[href]",
    );
    firstField?.focus();
    return () => previousFocusRef.current?.focus();
  }, []);

  // The OPPONENT'S logo — the only per-match upload now that the tenant
  // club's mark always comes from settings.club_logo_url. Held in
  // component state because it posts as multipart AFTER the match save
  // (the create flow needs an id before the upload).
  const [opponentLogoFile, setOpponentLogoFile] = useState<File | null>(null);
  const [logoBust, setLogoBust] = useState<number>(0);
  const opponentName = (
    form.watch("team_side") === "away"
      ? form.watch("home_team")
      : form.watch("away_team")
  ).trim() || "Opponent";
  const savedOpponentLogo = getOpponentLogoUrl(
    match,
    defaults.team_side === "away" ? "home" : "away",
  );

  const mutation = useMutation({
    mutationFn: async (values: MatchFormValues) => {
      // On UPDATE, only send the team-side ids when the coach
      // actually touched the radio — otherwise let the backend's
      // exclude_unset path preserve whatever was already there
      // (an opponent id set by the migration backfill, or by the
      // opposing team's coach via THEIR scope). On CREATE always
      // include — there's nothing to preserve yet.
      const teamSideDirty = form.formState.dirtyFields.team_side === true;
      const includeTeamSide = !isEdit || teamSideDirty;
      const payload = {
        ...cleanMatchPayload(values, effectiveTeamId, { includeTeamSide }),
        ...(values.competition === "tournament" && eventDraft
          ? { tournament_draft: eventDraft }
          : {}),
      };
      const savedMatch =
        isEdit && match
          ? await updateMatch(match.id, payload, match.updated_at, {
              teamId: effectiveTeamId,
            })
          : await createMatch(payload, { teamId: effectiveTeamId });
      let logoUploadError: unknown = null;
      if (opponentLogoFile) {
        try {
          await uploadMatchOpponentLogo(savedMatch.id, opponentLogoFile);
        } catch (err) {
          logoUploadError = err;
        }
      }
      return { savedMatch, logoUploadError };
    },
    onSuccess: async ({ savedMatch, logoUploadError }) => {
      await invalidateAllMatchesCaches(queryClient);
      if (!logoUploadError) {
        setOpponentLogoFile(null);
        setLogoBust(Date.now());
      }
      toast.success(isEdit ? "Match updated" : "Match created");
      if (logoUploadError) {
        toast.error("Opponent logo upload failed", {
          description: messageForApiError(
            logoUploadError,
            "Match saved, but the opponent logo could not be uploaded.",
          ),
        });
      }
      onSaved?.(savedMatch);
      onClose();
    },
    onError: (err) => {
      setError(messageForApiError(err, "Failed to save match."));
      toast.error(`Couldn't ${isEdit ? "update" : "create"} match`, {
        description: messageForApiError(err, "Failed to save match."),
      });
    },
  });

  const handleDialogKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key === "Escape" && !mutation.isPending) {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>(
        "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])",
      ) ?? [],
    ).filter((el) => !el.hasAttribute("disabled") && el.tabIndex !== -1);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const onSubmit: SubmitHandler<MatchFormValues> = (values) => {
    setError(null);
    if (hasExplicitTeamOptions && !effectiveTeamId) {
      setError("Pick a team for this match.");
      return;
    }
    mutation.mutate(values);
  };

  return (
    <div
      className="fixed inset-0 z-[140] flex items-end justify-center bg-black/80 p-3 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label={isEdit ? "Edit match" : "Add match"}
      data-testid="match-edit-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget && !mutation.isPending) onClose();
      }}
    >
      <form
        ref={dialogRef}
        onSubmit={form.handleSubmit(onSubmit)}
        onKeyDown={handleDialogKeyDown}
        className="flex max-h-[calc(100dvh-1.5rem)] w-full max-w-2xl flex-col gap-4 overflow-y-auto rounded border border-rule bg-surface p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-pop sm:p-5"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-display uppercase tracking-wider text-ink">
              {isEdit ? "Edit match" : "Add match"}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            aria-label="Close"
            data-testid="match-edit-modal-close"
            className="rounded border border-rule px-2 py-1 text-xs text-ink-muted hover:text-ink focus:outline-none focus:ring-2 focus:ring-accent"
          >
            <X size={18} aria-hidden />
          </button>
        </div>

        {error ? (
          <p
            data-testid="match-edit-modal-error"
            role="alert"
            className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          >
            {error}
          </p>
        ) : null}

        <div className="match-editor-body">
          <div className="match-editor-scope">
            {hasExplicitTeamOptions ? (
              <div className="md:col-span-2">
                <Field label="Team">
                  <TkticSelect
                    value={selectedTeamId}
                    onChange={(next) => {
                      const previousTeamName = effectiveTeamName;
                      setSelectedTeamId(next);
                      const nextTeam = sortedTeamOptions.find(
                        (team) => team.id === next,
                      );
                      if (
                        !isEdit &&
                        (!form.getValues("home_team") ||
                          form.getValues("home_team") === previousTeamName)
                      ) {
                        form.setValue("home_team", nextTeam?.name ?? "", {
                          shouldDirty: true,
                        });
                      }
                      form.setValue(
                        "team_side",
                        next ? "home" : "none",
                        { shouldDirty: !isEdit },
                      );
                    }}
                    data-testid="match-edit-modal-team"
                    ariaLabel="Team"
                    disabled={isEdit || sortedTeamOptions.length <= 1}
                    placeholder="Pick a team"
                    className="w-full"
                    options={teamSelectOptions}
                  />
                  {isEdit ? (
                    <span className="mt-1 block text-[0.6rem] font-mono text-ink-faint">
                      Existing match team is fixed after creation.
                    </span>
                  ) : null}
                </Field>
              </div>
            ) : (
              <Field label="Team">
                <div className="match-fixed-team">
                  <Shield size={18} aria-hidden />
                  <span>{effectiveTeamName || "Selected team"}</span>
                  <LockKeyhole size={16} aria-label="Team is fixed" />
                </div>
              </Field>
            )}
            {availableSeasons.length > 0 ? (
              <Field
                label="Season"
                error={form.formState.errors.season_id?.message}
              >
                <Controller
                  control={form.control}
                  name="season_id"
                  render={({ field }) => (
                    <TkticSelect
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      data-testid="match-season-select"
                      ariaLabel="Season"
                      disabled={availableSeasons.length === 1}
                      className="w-full"
                      options={availableSeasons.map((s) => ({
                        value: s.id,
                        label: s.name,
                      }))}
                    />
                  )}
                />
              </Field>
            ) : null}
          </div>
          <div className="match-editor-columns">
            <section className="match-editor-section">
              <h4>
                <Flag size={18} aria-hidden />
                The fixture
              </h4>
              <div className="match-editor-fields">
                <Field
                  label="Home team *"
                  error={form.formState.errors.home_team?.message}
                >
                  <input
                    {...form.register("home_team")}
                    data-testid="match-edit-modal-home"
                    className={fieldClassName}
                  />
                </Field>
                <Field
                  label="Away team *"
                  error={form.formState.errors.away_team?.message}
                >
                  <input
                    {...form.register("away_team")}
                    data-testid="match-edit-modal-away"
                    className={fieldClassName}
                  />
                </Field>
                {effectiveTeamId ? (
                  <div className="md:col-span-2">
                    <TeamSideField form={form} />
                  </div>
                ) : null}
                <Field label="Date" error={form.formState.errors.date?.message}>
                  <Controller
                    control={form.control}
                    name="date"
                    render={({ field }) => (
                      <TkticDateInput
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        data-testid="match-edit-modal-date"
                      />
                    )}
                  />
                </Field>
                <Field
                  label="Kick-off time"
                  error={form.formState.errors.time?.message}
                >
                  <input
                    {...form.register("time")}
                    data-testid="match-edit-modal-time"
                    placeholder="HH:MM"
                    className={fieldClassName}
                  />
                </Field>
                <Field
                  label="Match location"
                  error={form.formState.errors.location?.message}
                >
                  <input
                    {...form.register("location")}
                    data-testid="match-edit-modal-location"
                    placeholder="Pitch or venue"
                    className={fieldClassName}
                  />
                </Field>
                <div className="md:col-span-2">
                  <OpponentLogoField
                    name={opponentName}
                    savedLogoUrl={savedOpponentLogo ? `${savedOpponentLogo}?v=${logoBust}` : null}
                    file={opponentLogoFile}
                    onFile={setOpponentLogoFile}
                  />
                </div>
              </div>
            </section>
            <section className="match-editor-section">
              <h4>
                <Trophy size={18} aria-hidden />
                The competition
              </h4>
              <Field label="Competition">
                <Controller
                  control={form.control}
                  name="competition"
                  render={({ field }) => (
                    <TkticSelect
                      value={field.value ?? "league"}
                      onChange={(next) => {
                        field.onChange(next);
                        // Leaving "tournament" clears the tournament fields
                        // so a reclassified match can't keep a stale name —
                        // which would otherwise still group it into that
                        // tournament's section. The API rejects the
                        // combination too; this keeps the form from
                        // submitting something it would 422 on.
                        if (next !== "tournament") {
                          form.setValue("tournament_id", "", {
                            shouldDirty: true,
                          });
                          form.setValue("stage", "", { shouldDirty: true });
                          setEventDraft(undefined);
                          setEditingEvent(undefined);
                          form.setValue("tournament_name", "", {
                            shouldDirty: true,
                          });
                          form.setValue("tournament_outcome", "", {
                            shouldDirty: true,
                          });
                        }
                      }}
                      data-testid="match-edit-modal-competition"
                      ariaLabel="Competition"
                      className="w-full"
                      options={[
                        { value: "league", label: "League" },
                        { value: "tournament", label: "Tournament" },
                        { value: "friendly", label: "Friendly" },
                      ]}
                    />
                  )}
                />
              </Field>
              <TournamentFields
                form={form}
                knownTournaments={knownTournaments}
                events={events.data?.tournaments ?? []}
                draft={eventDraft}
                editing={editingEvent !== undefined}
                onSelect={() => setEventDraft(undefined)}
                onEdit={setEditingEvent}
              />
              {events.isError && form.watch("competition") === "tournament" ? (
                <p role="alert">
                  Could not load tournaments.{" "}
                  <button type="button" onClick={() => void events.refetch()}>
                    Retry
                  </button>
                </p>
              ) : null}
              {editingEvent !== undefined ? (
                <>
                  <TournamentEditor
                    key={editingEvent?.id ?? "new"}
                    draft={editingEvent ?? undefined}
                    onCancel={() => setEditingEvent(undefined)}
                    onStage={(draft) => {
                      setEventDraft(draft);
                      form.setValue("tournament_id", draft.id ?? "", {
                        shouldDirty: true,
                      });
                      form.setValue("tournament_name", "", { shouldDirty: true });
                      form.setValue("tournament_outcome", "", {
                        shouldDirty: true,
                      });
                      setEditingEvent(undefined);
                    }}
                  />
                  <Field label="Round / stage">
                    <input
                      {...form.register("stage")}
                      placeholder="e.g. Group A · Match 2, Semi-final"
                    />
                  </Field>
                </>
              ) : null}
            </section>
            <section className="match-editor-section">
              <h4>
                <ChartNoAxesColumnIncreasing size={18} aria-hidden />
                The result
              </h4>
              <Field
                label="Match status"
                error={form.formState.errors.match_status?.message}
              >
                <Controller
                  control={form.control}
                  name="match_status"
                  render={({ field }) => (
                    <TkticSelect
                      ariaLabel="Match status"
                      value={field.value}
                      onChange={field.onChange}
                      options={[
                        { value: "scheduled", label: "Scheduled" },
                        { value: "completed", label: "Completed" },
                        { value: "postponed", label: "Postponed" },
                        { value: "cancelled", label: "Cancelled" },
                      ]}
                    />
                  )}
                />
              </Field>
              <div className="match-score-fields">
                <Field
                  label="Home score"
                  error={form.formState.errors.score_home?.message}
                >
                  <input
                    {...form.register("score_home", {
                      setValueAs: parseOptionalScore,
                    })}
                    data-testid="match-edit-modal-score-home"
                    inputMode="numeric"
                    aria-label="Home score"
                    className={fieldClassName}
                  />
                </Field>
                <Field
                  label="Away score"
                  error={form.formState.errors.score_away?.message}
                >
                  <input
                    {...form.register("score_away", {
                      setValueAs: parseOptionalScore,
                    })}
                    data-testid="match-edit-modal-score-away"
                    inputMode="numeric"
                    aria-label="Away score"
                    className={fieldClassName}
                  />
                </Field>
              </div>
              <p className="text-xs text-ink-muted">
                Leave both scores blank until the result is final. Only
                completed matches count towards performance.
              </p>
              <PenaltyShootoutField form={form} />
            </section>
          </div>
          <section className="match-editor-recordings">
            <div className="match-editor-recording-layout">
              <h4>
                <Film size={18} aria-hidden />
                The recording
              </h4>
              <Field
                label="Video recording layout"
                error={form.formState.errors.format?.message}
              >
                {match && Object.values(match.video_status ?? {}).some(
                  (status) => status === "ready" || status === "transcoding",
                ) ? (
                  <div className="match-fixed-team" data-testid="match-edit-modal-format-locked">
                    <LockKeyhole size={17} aria-hidden="true" />
                    <span>{form.watch("format") === "separate" ? "Separate recordings" : "Full match"}</span>
                  </div>
                ) : (
                  <Controller
                    control={form.control}
                    name="format"
                    render={({ field }) => (
                      <TkticSelect
                        value={field.value ?? "full"}
                        onChange={field.onChange}
                        data-testid="match-edit-modal-format"
                        ariaLabel="Video recording layout"
                        className="w-full"
                        options={[
                          { value: "full", label: "Full match" },
                          { value: "separate", label: "Separate recordings" },
                        ]}
                      />
                    )}
                  />
                )}
              </Field>
              {match && Object.values(match.video_status ?? {}).some(
                (status) => status === "ready" || status === "transcoding",
              ) ? (
                <p className="match-editor-hint">
                  This match already has a recording. Its current video layout is retained.
                </p>
              ) : null}
            </div>
            <div className="match-editor-recording-preview">
              <div className="match-recording-options" aria-label="Recording slots">
                {slotsForMatch({ format: form.watch("format") }).map((slot) => (
                  <div key={slot}>
                    <span><Film size={16} aria-hidden="true" />{slot === "extra_time" ? "Extra time (overtime)" : SLOT_LABELS[slot]}</span>
                    {slot === "extra_time" || slot === "penalty_shootout" ? <small>Optional</small> : null}
                  </div>
                ))}
              </div>
              <p className="match-editor-hint">
                {form.watch("format") === "separate"
                  ? "Upload the parts you have. Extra time and shootout recordings are optional and do not affect whether a match is complete."
                  : "One continuous recording, including extra time and penalties if they took place."}
              </p>
            </div>
            <p className="match-editor-hint">
              Recording layout is separate from tournament format or 7v7 / 9v9 / 11v11. Upload after saving; a result does not require video.
            </p>
          </section>
        </div>

        <div className="match-editor-footer">
          <p>{isEdit ? "Save changes to this match." : "Add video after creating the match."}</p>
          <div>
            <Button type="button" onClick={onClose} disabled={mutation.isPending} variant="outline" size="sm">
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending || editingEvent !== undefined} data-testid="match-edit-modal-submit" size="sm">
              {mutation.isPending ? "Saving…" : isEdit ? "Save match" : "Create match"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}

/** Home / Away / Not on this team picker.
 *
 * Rendered as a styled chip radiogroup (the no-native-chrome rule
 * bans raw `<input type="radio">` per AGENTS.md). Each chip toggles
 * the `team_side` field on the parent form via `setValue`, then the
 * mutation folds the choice into `home_team_id` / `away_team_id`
 * via `cleanMatchPayload`.
 *
 * The "none" option is the legitimate "external scrimmage where
 * neither side is this tenant" answer — leaves both ids null. The
 * legacy string match still classifies the row for read-paths if
 * one of the display strings happens to match the team name.
 */
/**
 * Tournament name + outcome — shown only for a tournament match (0052).
 *
 * The name is free text with a `<datalist>` of names this team has
 * already used, so entering several matches during one weekend event is
 * a pick rather than a retype. It is deliberately NOT required: a coach
 * uploading video mid-tournament shouldn't be blocked on naming it, and
 * unnamed matches group under a generic heading they can name later.
 *
 * The outcome is one value per tournament stored on the match — a coach
 * sets it once, typically on the final, and the team page reads it
 * across the group by name.
 */
function TournamentFields({
  form,
  knownTournaments,
  events,
  draft,
  editing,
  onSelect,
  onEdit,
}: {
  form: ReturnType<typeof useForm<MatchFormValues>>;
  knownTournaments: string[];
  events: Tournament[];
  draft?: TournamentDraft;
  editing: boolean;
  onSelect: () => void;
  onEdit: (draft: TournamentDraft | null) => void;
}) {
  const competition = form.watch("competition");
  const eventId = form.watch("tournament_id");
  const event = events.find((e) => e.id === eventId);
  if (competition !== "tournament") {
    return (
      <p className="match-editor-hint">
        {competition === "friendly"
          ? "Friendly results stay separate from the league record."
          : "Completed league matches contribute to your team’s season record."}
      </p>
    );
  }
  const eventPicker = (
    <div className="match-event-picker">
      <Field label="Tournament">
        <TkticSelect
          ariaLabel="Tournament"
          value={eventId}
          onChange={(id) => {
            onSelect();
            form.setValue("tournament_id", id, { shouldDirty: true });
          }}
          options={[
            { value: "", label: draft?.name ?? "No linked tournament" },
            ...events.map((e) => ({
              value: e.id,
              label: e.id === draft?.id ? draft.name : e.name,
            })),
          ]}
        />
      </Field>
      <Button type="button" variant="ghost" onClick={() => onEdit(null)}>
        <Plus size={16} aria-hidden="true" />
        New tournament
      </Button>
    </div>
  );

  if (editing) return eventPicker;

  const stageField = (
    <Field label="Round / stage">
      <input
        {...form.register("stage")}
        placeholder="e.g. Group A · Match 2, Semi-final"
      />
    </Field>
  );

  if (event || draft) {
    const selected = draft ?? event!;
    return (
      <>
        {eventPicker}
        <div className="match-event-summary">
          <strong>{selected.name}</strong>
          <p className="match-editor-hint">
            {[selected.start_date, selected.end_date]
              .filter(Boolean)
              .join(" – ") || "Dates not set"}
            {selected.location ? ` · ${selected.location}` : ""}
          </p>
          {draft ? (
            <p className="match-editor-hint">
              Tournament changes save together with this match.
            </p>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            onClick={() => onEdit(selected)}
          >
            Edit tournament
          </Button>
        </div>
        {stageField}
      </>
    );
  }

  return (
    <>
      {eventPicker}
      <Field
        label="Tournament name (optional)"
        error={form.formState.errors.tournament_name?.message}
      >
        <input
          {...form.register("tournament_name")}
          list="match-edit-modal-tournament-names"
          data-testid="match-edit-modal-tournament-name"
          placeholder="Name it now or add it later"
          className="w-full rounded-control border border-rule bg-canvas px-2 py-1.5 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent"
        />
        {/* Suggestions only — the field stays free text, so a new
         * tournament needs no setup step. */}
        <datalist id="match-edit-modal-tournament-names">
          {knownTournaments.map((name) => (
            <option key={name} value={name} />
          ))}
        </datalist>
      </Field>
      <p className="match-editor-hint">
        Link a tournament to keep its dates, rounds and overall result
        together.
      </p>
      {stageField}
      <Field label="Overall tournament finish">
        <Controller
          control={form.control}
          name="tournament_outcome"
          render={({ field }) => (
            <TkticSelect
              value={field.value ?? ""}
              onChange={field.onChange}
              data-testid="match-edit-modal-tournament-outcome"
              ariaLabel="Overall tournament finish"
              className="w-full"
              options={[
                { value: "", label: "Not recorded" },
                { value: "champions", label: "Champions" },
                { value: "finalist", label: "Finalist" },
                { value: "semi_finalist", label: "Semi-finalist" },
                { value: "third_place", label: "Third place" },
                { value: "group_stage", label: "Group stage" },
              ]}
            />
          )}
        />
      </Field>
    </>
  );
}

/**
 * Penalty shootout — conditional instrumentation under the score line.
 *
 * Only rendered when regulation ended level, because that's the only
 * situation where a shootout is possible. A coach entering 3–1 never
 * sees these controls; there are no dead inputs and no "N/A" state.
 *
 * The explicit checkbox matters: a level score does NOT imply a
 * shootout (most drawn matches simply end), so intent has to be stated
 * rather than inferred from whether someone typed numbers.
 *
 * The live outcome line resolves the question the feature exists to
 * answer — who actually won — before the coach saves. The note beneath
 * it sets expectations at entry time: this still records as a draw, so
 * nobody is surprised when the standings don't show a win.
 *
 * Design-system notes: an inset well (`bg-elevated` + `rounded-inset`)
 * rather than a nested card, per the no-cards-inside-cards rule; the
 * inputs keep `rounded-control` so the 8px control sits inside the
 * quieter panel.
 */
function PenaltyShootoutField({
  form,
}: {
  form: ReturnType<typeof useForm<MatchFormValues>>;
}) {
  const scoreHome = form.watch("score_home");
  const scoreAway = form.watch("score_away");
  const wentToPens = form.watch("went_to_pens");
  const pkHome = form.watch("pk_home");
  const pkAway = form.watch("pk_away");
  const homeTeam = form.watch("home_team");
  const awayTeam = form.watch("away_team");

  const level = isLevelScore(scoreHome, scoreAway);

  const setWentToPens = (next: boolean) => {
    form.setValue("went_to_pens", next, { shouldDirty: true });
    // Turning it off clears the tallies so a stale shootout can't
    // survive in form state and get re-sent on save.
    if (!next) {
      form.setValue("pk_home", null, { shouldDirty: true });
      form.setValue("pk_away", null, { shouldDirty: true });
    }
  };

  // Roving-tabindex arrow nav, matching `TeamSideField`'s chip group.
  const handleToggleKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    const move = (delta: number) => {
      event.preventDefault();
      const next =
        PK_TOGGLE_OPTIONS[
          (index + delta + PK_TOGGLE_OPTIONS.length) % PK_TOGGLE_OPTIONS.length
        ];
      setWentToPens(next.value);
      document
        .querySelector<HTMLButtonElement>(
          `[data-testid="match-edit-modal-pk-toggle-${next.label.toLowerCase()}"]`,
        )
        ?.focus();
    };
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        move(1);
        return;
      case "ArrowLeft":
      case "ArrowUp":
        move(-1);
        return;
      default:
        return;
    }
  };

  // Collapse the whole block when the match wasn't level. The stored
  // values are cleared by `cleanMatchPayload` on save, so a score
  // corrected away from a draw can't leave an orphaned shootout.
  //
  // NOTE: this early return sits AFTER the hooks/handlers above so the
  // hook call order stays stable across renders.
  if (!level) return null;

  const error =
    form.formState.errors.pk_away?.message ??
    form.formState.errors.pk_home?.message;
  const winnerText = wentToPens
    ? shootoutWinnerText(pkHome, pkAway, homeTeam, awayTeam)
    : null;

  return (
    <div
      data-testid="match-edit-modal-pk-block"
      className="match-penalty-block"
    >
      {/* A chip group, not a native checkbox: AGENTS.md bans native
       * browser chrome for visible controls ("checkboxes/radios are
       * styled toggles or chip groups"). Mirrors `TeamSideField`
       * below — `role="radiogroup"` with roving tabindex and arrow-key
       * nav — so both toggles in this modal behave identically. */}
      <div className="flex flex-col gap-1 text-xs text-ink-muted">
        <span className="font-display uppercase tracking-wide">
          Decided by a penalty shootout?
        </span>
        <div
          role="radiogroup"
          aria-label="Decided by a penalty shootout?"
          data-testid="match-edit-modal-pk-toggle"
          className="inline-flex w-fit rounded border border-rule bg-canvas p-0.5"
        >
          {PK_TOGGLE_OPTIONS.map((opt, index) => {
            const active = wentToPens === opt.value;
            return (
              <button
                key={opt.label}
                type="button"
                role="radio"
                aria-checked={active}
                tabIndex={active ? 0 : -1}
                onClick={() => setWentToPens(opt.value)}
                onKeyDown={(e) => handleToggleKeyDown(e, index)}
                data-testid={`match-edit-modal-pk-toggle-${opt.label.toLowerCase()}`}
                data-active={active ? "true" : undefined}
                className="rounded px-3 py-1 text-xs font-display uppercase tracking-wider text-ink-muted hover:text-ink data-[active=true]:bg-accent data-[active=true]:text-accent-ink transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {wentToPens ? (
        <div className="match-penalty-fields">
          <div className="match-score-fields">
            <Field label="Home penalties">
              <input
                {...form.register("pk_home", { setValueAs: parseOptionalScore })}
                data-testid="match-edit-modal-pk-home"
                inputMode="numeric"
                aria-label="Home penalties"
                className={fieldClassName}
              />
            </Field>
            <Field label="Away penalties">
              <input
                {...form.register("pk_away", { setValueAs: parseOptionalScore })}
                data-testid="match-edit-modal-pk-away"
                inputMode="numeric"
                aria-label="Away penalties"
                className={fieldClassName}
              />
            </Field>
          </div>
          {error ? (
            <span
              data-testid="match-edit-modal-pk-error"
              className="font-mono text-[0.65rem] text-destructive"
            >
              {error}
            </span>
          ) : null}
          <div className="match-penalty-summary">
            {winnerText ? (
              <span
                data-testid="match-edit-modal-pk-winner"
                className="rounded-control border border-good/40 bg-good/10 px-2 py-1 text-[0.65rem] text-good"
              >
                {winnerText}
              </span>
            ) : null}
            <span className="match-editor-hint">
              Enter shootout tallies separately. They do not add to goals for
              or against.
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function TeamSideField({
  form,
}: {
  form: ReturnType<typeof useForm<MatchFormValues>>;
}) {
  const value = form.watch("team_side");
  const label = "Your team plays";
  const options: Array<{ value: TeamSide; label: string }> = [
    { value: "home", label: "Home" },
    { value: "away", label: "Away" },
    { value: "none", label: "Neither side" },
  ];
  // WAI-ARIA radiogroup with roving tabindex + arrow / Home / End
  // keyboard nav (same pattern as M7.5's ToneChipGroup, referenced
  // as the canonical chip-radiogroup template in AGENTS.md). Only
  // the currently-selected chip is in the tab order; arrow keys
  // move focus + select the next chip; Home/End jump to ends.
  const selectAndFocus = (index: number) => {
    const next = options[(index + options.length) % options.length];
    form.setValue("team_side", next.value, { shouldDirty: true });
    // Focus the newly-selected chip so keyboard users can keep
    // arrowing without the focus jumping back to the prior chip.
    const id = `match-edit-modal-team-side-${next.value}`;
    const el = document.querySelector<HTMLButtonElement>(
      `[data-testid="${id}"]`,
    );
    el?.focus();
  };
  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        event.preventDefault();
        selectAndFocus(index + 1);
        return;
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault();
        selectAndFocus(index - 1);
        return;
      case "Home":
        event.preventDefault();
        selectAndFocus(0);
        return;
      case "End":
        event.preventDefault();
        selectAndFocus(options.length - 1);
        return;
      default:
        return;
    }
  };
  return (
    <label className="flex flex-col gap-1 text-xs text-ink-muted">
      <span className="font-display uppercase tracking-wide">{label}</span>
      <div
        role="radiogroup"
        aria-label={label}
        data-testid="match-edit-modal-team-side"
        className="inline-flex w-fit rounded border border-rule bg-canvas p-0.5"
      >
        {options.map((opt, index) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={active}
              // Roving tabindex: only the active chip is reachable
              // via Tab; arrow keys move within the group.
              tabIndex={active ? 0 : -1}
              onClick={() =>
                form.setValue("team_side", opt.value, {
                  shouldDirty: true,
                })
              }
              onKeyDown={(e) => handleKeyDown(e, index)}
              data-testid={`match-edit-modal-team-side-${opt.value}`}
              data-active={active ? "true" : undefined}
              className="rounded px-3 py-1 text-xs font-display uppercase tracking-wider text-ink-muted hover:text-ink data-[active=true]:bg-accent data-[active=true]:text-accent-ink transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </label>
  );
}
